import { DateTime } from 'luxon';
import type { TFunction } from 'i18next';
import { matchIsValidTel } from 'mui-tel-input';
import z from 'zod';

import { PHONE_VALIDATION_COUNTRIES } from '@/data/constants';

import type { Passenger } from '@/data/models/passenger.model';
import type { TransportationAttribute } from '@/data/models/transportation-attribute.model';
import { TripLegType, type TripLeg } from '@/data/models/reservation.model';

import { useDefaultTranslation } from '@/hooks/useCommonTranslation';

import dateUtils from '@/services/DateUtils';
import { phoneUtils } from '@/services/phoneUtils';
import reservationUtils from '@/services/reservationUtils';
import { standingOrderNumberOfReservations } from '../machines/standingOrder.machine';
import {
  isWithinPickupDateRange,
  isWithinReturnDateRange,
} from '../utils/pickerDateTime';

const isValidUSPhone = (message: string) =>
  z
    .string()
    .refine(
      (v) =>
        matchIsValidTel(v, { onlyCountries: PHONE_VALIDATION_COUNTRIES }) &&
        v !== '+1',
      { message },
    );

const isOptionalValidUSPhone = (message: string) =>
  z
    .string()
    .optional()
    .default('')
    .refine(
      (v) => {
        const value = v ?? '';
        if (!value || value === '+1') return true;
        return matchIsValidTel(value, {
          onlyCountries: PHONE_VALIDATION_COUNTRIES,
        });
      },
      { message },
    );

/** Google Places / passenger autocomplete value (see AddressInput PlaceType) */
const placeTypeSchema = z
  .object({
    description: z.string().optional(),
    mainText: z.string().optional(),
    secondaryText: z.string().optional(),
    mainTextMatchedSubstrings: z
      .array(z.object({ offset: z.number(), length: z.number() }))
      .optional(),
    place_id: z.string().optional(),
    zipCode: z.string().optional(),
    isHeader: z.boolean().optional(),
  })
  .catchall(z.unknown());

const addressTripFieldSchema = (requiredMessage: string) =>
  z.union([z.string(), placeTypeSchema]).superRefine((val, ctx) => {
    if (typeof val === 'string') {
      if (!val.trim()) {
        ctx.addIssue({ code: 'custom', message: requiredMessage });
      }
      return;
    }
    if (val.isHeader === true || val.place_id === 'header') {
      ctx.addIssue({ code: 'custom', message: requiredMessage });
      return;
    }
    const hasAddress =
      Boolean(val.place_id?.trim()) ||
      Boolean(val.mainText?.trim()) ||
      Boolean(val.description?.trim());
    if (!hasAddress) {
      ctx.addIssue({ code: 'custom', message: requiredMessage });
    }
  });

const standingOrderDaySchema = z.object({
  enabled: z.boolean().optional().default(false),
  appointmentTime: z
    .union([
      z.null(),
      z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
    ])
    .optional(),
  pickupTime: z
    .union([
      z.null(),
      z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
    ])
    .optional(),
});

/** Mon–Sun, matching `standingOrderDayConfigs` / `enabledValues` order. */
const STANDING_ORDER_DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const standingOrderDaysSchema = z.object({
  monday: standingOrderDaySchema,
  tuesday: standingOrderDaySchema,
  wednesday: standingOrderDaySchema,
  thursday: standingOrderDaySchema,
  friday: standingOrderDaySchema,
  saturday: standingOrderDaySchema,
  sunday: standingOrderDaySchema,
});

const tripSegmentSchema = z
  .object({
    tripLegType: z.enum(TripLegType),
    reasonForTrip: z.string().min(1, 'Reason for trip is required'),
    appointmentDate: z.preprocess((val) => {
      if (val === null || val === undefined) return '';
      if (val instanceof DateTime && val.isValid) return val.toISODate();
      return val;
    }, z.string().optional().default('')),
    isWillCall: z.boolean(),
    appointmentDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    pickupDate: z.preprocess((val) => {
      if (val === null || val === undefined) return '';
      if (val instanceof DateTime && val.isValid) return val.toISODate();
      return val;
    }, z.string().optional().default('')),
    pickupDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    pickupTimeZone: z.string().default(''),
    dropoffTimeZone: z.string().default(''),
    pickupAddress: addressTripFieldSchema('Pickup address is required'),
    dropoffAddress: addressTripFieldSchema('Drop-off address is required'),
    pickupAddressStruct: z.string().optional(),
    dropoffAddressStruct: z.string().optional(),
    pickupPhone: isValidUSPhone('Pickup phone is required'),
    pickupPhoneExtension: z.string().optional().default(''),
    dropoffPhone: isOptionalValidUSPhone('Drop-off phone is invalid'),
    dropoffPhoneExtension: z.string().optional().default(''),
    pickupNotes: z.string().optional().default(''),
    dropoffNotes: z.string().optional().default(''),
    roundTripWillCall: z.boolean(),
    roundTripReturnDate: z.preprocess((val) => {
      if (val === null || val === undefined) return '';
      if (val instanceof DateTime && val.isValid) return val.toISODate();
      return val;
    }, z.string().optional().default('')),
    roundTripReturnDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    standingOrder: z.boolean().optional().default(false),
    standingOrderStartDate: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    standingOrderEndDate: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    standingOrderDays: standingOrderDaysSchema.optional(),
    passengerMobility: z.string().min(1, 'Passenger mobility is required'),
    passengerAssistance: z.string().optional(),
    AttributeLevel1: z.string().min(1, 'Attribute level 1 is required'),
    AttributeLevel2: z.string().optional().default(''),
    AttributeLevel3: z.string().optional(),
    AttributeLevel4: z.string().optional(),
    AttributeLevel5: z.string().optional(),
    AttributeLevel6: z.string().optional(),
    MobilityAttribute: z.string().min(1, 'Mobility attribute is required'),
    height: z.string().optional().default(''),
    weight: z.string().optional().default(''),
    reservationReferenceID: z.string().optional(),
    tripLegReferenceID: z.string().optional(),
    roundTripTripLegReferenceID: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isWillCall) {
      if (!data.appointmentDate || data.appointmentDate.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Appointment date is required for will-call trips',
          path: ['appointmentDate'],
        });
        return;
      }
      const willCallDate = DateTime.fromISO(data.appointmentDate, {
        zone: dateUtils.WORK_TIME_ZONE,
      });
      const today = DateTime.now()
        .setZone(dateUtils.WORK_TIME_ZONE)
        .startOf('day');
      if (willCallDate.isValid && willCallDate.startOf('day') < today) {
        ctx.addIssue({
          code: 'custom',
          message: 'Appointment date cannot be in the past',
          path: ['appointmentDate'],
        });
      }
      return;
    }

    const appt = data.appointmentDateTime;
    if (
      appt === null ||
      appt === undefined ||
      !(appt instanceof DateTime && appt.isValid)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Appointment date and time is required',
        path: ['appointmentDateTime'],
      });
    }
    const pickup = data.pickupDateTime;
    if (
      pickup === null ||
      pickup === undefined ||
      !(pickup instanceof DateTime && pickup.isValid)
    ) {
      // Pickup time isn't set yet, but a date may already be — typing an
      // out-of-range date alone (no time) stores it on the paired
      // `pickupDate` string while `pickupDateTime` stays null (see
      // TripArrayFieldDateTimeInput.tsx). react-hook-form uses this
      // resolver exclusively once a resolver is attached (production
      // dialogs all attach one via useReservationFormResolver), so the
      // picker's own rules.validate never runs there — this schema check
      // is the only thing that can catch it. Its range error takes
      // precedence over the missing-time error so the mismatch is visible
      // before a time is even entered.
      const pickupDateOnly =
        typeof data.pickupDate === 'string' && data.pickupDate.trim()
          ? DateTime.fromISO(data.pickupDate)
          : null;
      if (
        pickupDateOnly?.isValid &&
        appt instanceof DateTime &&
        appt.isValid &&
        !isWithinPickupDateRange(pickupDateOnly, appt)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: useDefaultTranslation('reservation.pickupDateInvalidError'),
          path: ['pickupDateTime'],
        });
      } else {
        ctx.addIssue({
          code: 'custom',
          message: 'Pickup date and time is required',
          path: ['pickupDateTime'],
        });
      }
    } else if (
      appt instanceof DateTime &&
      appt.isValid &&
      !isWithinPickupDateRange(pickup, appt)
    ) {
      // This is a submit-time backstop for pickup values set outside the
      // date picker (auto pickup calculation, Edit-flow defaults) — the
      // picker's own field-level validation (TripArrayFieldDateTimeInput.tsx)
      // shows the same message as soon as the user types or blurs. The
      // blocking field error always uses the instructive wording; the
      // plain "different than appointment date" wording is reserved for
      // the separate, non-blocking PickupDateMismatchMessage.
      ctx.addIssue({
        code: 'custom',
        message: useDefaultTranslation('reservation.pickupDateInvalidError'),
        path: ['pickupDateTime'],
      });
    }

    if (!data.roundTripWillCall) {
      const returnTime = data.roundTripReturnDateTime;
      if (
        returnTime === null ||
        returnTime === undefined ||
        !(returnTime instanceof DateTime && returnTime.isValid)
      ) {
        // Return time isn't set yet, but a date may already be — typing an
        // out-of-range date alone (no time) stores it on the paired
        // `roundTripReturnDate` string while `roundTripReturnDateTime`
        // stays null (see RoundTripReturnTripFields.tsx). Same precedence
        // and distinct, more instructive message as the pickup check above.
        const returnDateOnly =
          typeof data.roundTripReturnDate === 'string' &&
          data.roundTripReturnDate.trim()
            ? DateTime.fromISO(data.roundTripReturnDate)
            : null;
        if (
          returnDateOnly?.isValid &&
          appt instanceof DateTime &&
          appt.isValid &&
          !isWithinReturnDateRange(returnDateOnly, appt)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: useDefaultTranslation(
              'reservation.returnDateInvalidError',
            ),
            path: ['roundTripReturnDateTime'],
          });
        } else {
          ctx.addIssue({
            code: 'custom',
            message: 'Return trip date and time is required',
            path: ['roundTripReturnDateTime'],
          });
        }
      } else if (
        appt instanceof DateTime &&
        appt.isValid &&
        returnTime < appt
      ) {
        ctx.addIssue({
          code: 'custom',
          message: useDefaultTranslation(
            'reservation.returnDateBeforePickupError',
          ),
          path: ['roundTripReturnDateTime'],
        });
      } else if (
        appt instanceof DateTime &&
        appt.isValid &&
        !isWithinReturnDateRange(returnTime, appt)
      ) {
        // Submit-time backstop, same as the pickup check above. The
        // blocking field error always uses the instructive wording; the
        // plain "different than appointment date" wording is reserved for
        // the separate, non-blocking ReturnDateMismatchMessage.
        ctx.addIssue({
          code: 'custom',
          message: useDefaultTranslation('reservation.returnDateInvalidError'),
          path: ['roundTripReturnDateTime'],
        });
      }
    }
  })
  .superRefine((data, ctx) => {
    // F1V2-6666
    // Without an end date or at least one weekday the create payload carries
    // no usable schedule and the API rejects it with a 400, so block
    // until the standing order is complete. MultiLeg hides the standing order
    // controls (a stale `standingOrder: true` can survive the trip type
    // switch) and create drops the standing order for it, so skip it there.
    if (!data.standingOrder || data.tripLegType === TripLegType.MultiLeg) {
      return;
    }

    const start = data.standingOrderStartDate;
    const end = data.standingOrderEndDate;
    if (!(end instanceof DateTime && end.isValid)) {
      ctx.addIssue({
        code: 'custom',
        message: useDefaultTranslation('reservation.requiredField'),
        path: ['standingOrderEndDate'],
      });
    } else if (start instanceof DateTime && start.isValid) {
      // Same range rules StandingOrderForm shows under the End date picker.
      const daysDiff = end.diff(start, 'days').days;
      if (daysDiff <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: useDefaultTranslation(
            'reservation.endDateMustBeAfterStartDate',
          ),
          path: ['standingOrderEndDate'],
        });
      } else if (daysDiff > 90) {
        ctx.addIssue({
          code: 'custom',
          message: useDefaultTranslation('reservation.max90'),
          path: ['standingOrderEndDate'],
        });
      }
    }

    const enabledValues = STANDING_ORDER_DAY_KEYS.map(
      (day) => !!data.standingOrderDays?.[day]?.enabled,
    );
    if (!enabledValues.some(Boolean)) {
      ctx.addIssue({
        code: 'custom',
        message: useDefaultTranslation('reservation.standingOrderDayRequired'),
        path: ['standingOrderDays'],
      });
    } else if (
      start instanceof DateTime &&
      start.isValid &&
      end instanceof DateTime &&
      end.isValid &&
      standingOrderNumberOfReservations({
        standingOrderStartDate: start,
        standingOrderEndDate: end,
        enabledValues,
      }) === 0
    ) {
      // Every selected weekday falls outside the date range, so the
      // standing order would book zero reservations.
      ctx.addIssue({
        code: 'custom',
        message: useDefaultTranslation(
          'reservation.standingOrderDayOutOfRange',
        ),
        path: ['standingOrderDays'],
      });
    }
  });

const isAppointmentDateRequired = (isWillCall: boolean) => isWillCall;

const isAppointmentDateTimeRequired = (isWillCall: boolean) => !isWillCall;

const isPickupDateTimeRequired = (isWillCall: boolean) => !isWillCall;

type TripSegmentValues = z.infer<typeof tripSegmentSchema>;

const DEFAULT_TRIP_TYPE = TripLegType.RoundTrip;

const emptyTripSegment: z.input<typeof tripSegmentSchema> = {
  tripLegType: DEFAULT_TRIP_TYPE,
  reasonForTrip: '',
  appointmentDate: null,
  isWillCall: false,
  appointmentDateTime: null,
  pickupDate: null,
  pickupDateTime: null,
  pickupAddress: '',
  dropoffAddress: '',
  pickupPhone: '',
  pickupPhoneExtension: '',
  dropoffPhone: '',
  dropoffPhoneExtension: '',
  pickupNotes: '',
  dropoffNotes: '',
  roundTripWillCall: true,
  roundTripReturnDate: null,
  roundTripReturnDateTime: null,
  standingOrder: false,
  standingOrderStartDate: null,
  standingOrderEndDate: null,
  standingOrderDays: {
    monday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    tuesday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    wednesday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    thursday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    friday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    saturday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    sunday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
  },
  passengerMobility: '',
  passengerAssistance: '',
  AttributeLevel1: '',
  AttributeLevel2: '',
  AttributeLevel3: '',
  AttributeLevel4: '',
  AttributeLevel5: '',
  AttributeLevel6: '',
  MobilityAttribute: '',
  height: '',
  weight: '',
};

/**
 * When the reverse map includes GURNEY / BARIATRIC_GURNEY UUIDs, member weight is
 * required for trips whose AttributeLevel2 matches. Otherwise (e.g. machine-only
 * parse without the map), non-empty height indicates the gurney measurements UI.
 */
function tripRequiresMemberWeight(
  trip: { AttributeLevel2: string; height: string },
  reverseTransportationAttributeMap: Record<string, string> | null | undefined,
): boolean {
  const gurneyUuid = reverseTransportationAttributeMap?.['GURNEY'];
  const bariatricGurneyUuid =
    reverseTransportationAttributeMap?.['BARIATRIC_GURNEY'];
  const hasGurneyCodesInMap =
    gurneyUuid !== undefined || bariatricGurneyUuid !== undefined;

  if (hasGurneyCodesInMap) {
    return (
      (gurneyUuid !== undefined && trip.AttributeLevel2 === gurneyUuid) ||
      (bariatricGurneyUuid !== undefined &&
        trip.AttributeLevel2 === bariatricGurneyUuid)
    );
  }
  return Boolean(trip.height?.trim());
}

export function getDefaultPickupPhoneAndExtensionFromPassenger(
  passenger: Passenger | null | undefined,
): { pickupPhone: string; pickupPhoneExtension: string } {
  if (!passenger) {
    return { pickupPhone: '', pickupPhoneExtension: '' };
  }
  const pickupPhone = reservationUtils.getPassengerPhone(passenger);
  return {
    pickupPhone: pickupPhone?.phoneNumber ? pickupPhone.phoneNumber : '',
    pickupPhoneExtension: pickupPhone?.phoneExtension || '',
  };
}

const MEMBER_FORM_PHONE_FIELDS = [
  'MemberHomePhone',
  'MemberMobilePhone',
  'AlternateContactPhone',
] as const;

type CreateReservationFormSchemaOptions = {
  /** When false, member phone fields accept any string (trip-details step only). */
  validateMemberPhones?: boolean;
};

function createReservationFormSchema(
  reverseTransportationAttributeMap?: Record<string, string> | null,
  t?: TFunction<'common'>,
  isNonAmbulatoryByIndex?: boolean[] | null,
  options?: CreateReservationFormSchemaOptions,
) {
  const translate = t ?? useDefaultTranslation;
  const validateMemberPhones = options?.validateMemberPhones !== false;
  const optionalPhoneFieldValidation = validateMemberPhones
    ? isOptionalValidUSPhone(translate('common.invalidPhone'))
    : z.string().optional().default('');

  return z
    .object({
      tripType: z.enum(TripLegType),
      genericErrors: z.string().optional().default(''),
      MemberHomePhone: optionalPhoneFieldValidation,
      MemberMobilePhone: optionalPhoneFieldValidation,
      AllowText: z.boolean().optional().default(false),
      ShowOtherInputs: z.boolean().optional().default(false),
      AlternateContact: z.boolean().optional().default(false),
      AlternateContactType: z.string().optional().default(''),
      AlternateContactFirstName: z.string().optional().default(''),
      AlternateContactLastName: z.string().optional().default(''),
      AlternateContactPhone: optionalPhoneFieldValidation,
      AlternateContactAllowSMS: z.boolean().optional().default(false),
      AdditionalPassengers: z.boolean().optional().default(false),
      AdditionalPassengerCount: z.number().nullable().optional().default(null),
      AdditionalAttributes: z.boolean().optional().default(false),
      AllowOverride: z.boolean().optional().default(false),
      OverrideFirstName: z.string().optional().default(''),
      OverrideLastName: z.string().optional().default(''),
      OverrideReason: z.string().optional().default(''),
      OverrideServicePlan: z.string().optional().default(''),
      RequestedProvider: z.boolean().optional().default(false),
      RequestedTransportationProviderID: z
        .number()
        .nullable()
        .optional()
        .default(null),
      PassengerNoteTypes: z.array(z.string()).optional().default([]),
      DriverNoteTypes: z.array(z.string()).optional().default([]),
      trips: z.array(tripSegmentSchema).min(1, 'At least one trip is required'),
    })
    .superRefine((data, ctx) => {
      if (data.tripType !== TripLegType.MultiLeg && data.trips.length !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'One-way mode expects exactly one trip segment',
          path: ['trips'],
        });
      }
    })
    .superRefine((data, ctx) => {
      data.trips.forEach((trip, idx) => {
        if (trip.tripLegType !== data.tripType) {
          ctx.addIssue({
            code: 'custom',
            message: 'Trip leg type must match the selected trip type',
            path: ['trips', idx, 'tripLegType'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      data.trips.forEach((trip, tripIndex) => {
        if (
          !tripRequiresMemberWeight(trip, reverseTransportationAttributeMap)
        ) {
          return;
        }
        const w = String(trip.weight ?? '').trim();
        if (!w) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.requiredField'),
            path: ['trips', tripIndex, 'weight'],
          });
          return;
        }
        const num = parseInt(w, 10);
        if (Number.isNaN(num) || num < 1) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.memberWeightPositive'),
            path: ['trips', tripIndex, 'weight'],
          });
          return;
        }
        if (num > 999) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.weightValidation'),
            path: ['trips', tripIndex, 'weight'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      if (isNonAmbulatoryByIndex == null) return;
      data.trips.forEach((trip, tripIndex) => {
        if (isNonAmbulatoryByIndex[tripIndex] !== true) return;
        if (!String(trip.AttributeLevel2 ?? '').trim()) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.requiredField'),
            path: ['trips', tripIndex, 'AttributeLevel2'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      if (!data.AlternateContact) return;

      const requiredMessage = translate('common.requiredField');
      const invalidPhoneMessage = translate('common.invalidPhone');

      if (!String(data.AlternateContactType ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactType'],
        });
      }
      if (!String(data.AlternateContactFirstName ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactFirstName'],
        });
      }
      if (!String(data.AlternateContactLastName ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactLastName'],
        });
      }

      const phone = String(data.AlternateContactPhone ?? '').trim();
      if (!phone || phone === '+1') {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactPhone'],
        });
      } else if (
        !matchIsValidTel(phone, { onlyCountries: PHONE_VALIDATION_COUNTRIES })
      ) {
        ctx.addIssue({
          code: 'custom',
          message: invalidPhoneMessage,
          path: ['AlternateContactPhone'],
        });
      }
    });
}

const reservationFormSchema = createReservationFormSchema(null);

const tripLegReservationFormSchema = createReservationFormSchema(
  null,
  undefined,
  undefined,
  { validateMemberPhones: false },
);

function createTripLegReservationFormSchema(
  reverseTransportationAttributeMap?: Record<string, string> | null,
  t?: TFunction<'common'>,
  isNonAmbulatoryByIndex?: boolean[] | null,
) {
  return createReservationFormSchema(
    reverseTransportationAttributeMap,
    t,
    isNonAmbulatoryByIndex,
    { validateMemberPhones: false },
  );
}

type CreatedReservationFormSchema = ReturnType<
  typeof createReservationFormSchema
>;

type ReservationFormValues = z.infer<typeof reservationFormSchema>;
type ReservationFormInput = z.input<typeof reservationFormSchema>;

const emptyReservationFormValues: ReservationFormInput = {
  tripType: DEFAULT_TRIP_TYPE,
  genericErrors: '',
  MemberHomePhone: '',
  MemberMobilePhone: '',
  AllowText: false,
  ShowOtherInputs: false,
  AlternateContact: false,
  AlternateContactType: '',
  AlternateContactFirstName: '',
  AlternateContactLastName: '',
  AlternateContactPhone: '',
  AlternateContactAllowSMS: false,
  AdditionalPassengers: false,
  AdditionalPassengerCount: null,
  AdditionalAttributes: false,
  AllowOverride: false,
  OverrideFirstName: '',
  OverrideLastName: '',
  OverrideReason: '',
  OverrideServicePlan: '',
  RequestedProvider: false,
  RequestedTransportationProviderID: null,
  PassengerNoteTypes: [],
  DriverNoteTypes: [],
  trips: [emptyTripSegment],
};

function mapLegacyLegDataToTripSegment(
  legData: Record<string, unknown>,
  tripType: TripLegType,
): ReservationFormInput['trips'][number] {
  const isWillCall = Boolean(legData.WillCall);
  const appointmentTime = legData.AppointmentTime as
    | DateTime
    | null
    | undefined;
  const pickupTime = legData.PickupTime as DateTime | null | undefined;
  const roundTripReturnTime = legData.RoundTripReturnTime as
    | DateTime
    | null
    | undefined;

  const appointmentDate =
    typeof legData.AppointmentDate === 'string' &&
    legData.AppointmentDate.trim()
      ? legData.AppointmentDate
      : appointmentTime?.isValid
        ? (appointmentTime.toISODate() ?? '')
        : '';

  return {
    ...emptyTripSegment,
    tripLegType: tripType,
    reasonForTrip: String(legData.TripPurpose ?? ''),
    isWillCall,
    appointmentDate: isWillCall ? appointmentDate : (appointmentDate ?? ''),
    appointmentDateTime: isWillCall
      ? null
      : appointmentTime?.isValid
        ? appointmentTime
        : null,
    pickupDate:
      !isWillCall && pickupTime?.isValid ? (pickupTime.toISODate() ?? '') : '',
    pickupDateTime: isWillCall ? null : pickupTime?.isValid ? pickupTime : null,
    pickupTimeZone: String(legData.PickupTimeZone ?? ''),
    dropoffTimeZone: String(legData.AppointmentTimeZone ?? ''),
    pickupAddress: (legData.PickupAddress ??
      '') as ReservationFormInput['trips'][number]['pickupAddress'],
    dropoffAddress: (legData.DropoffAddress ??
      '') as ReservationFormInput['trips'][number]['dropoffAddress'],
    pickupAddressStruct: legData.PickupAddressStruct
      ? String(legData.PickupAddressStruct)
      : undefined,
    dropoffAddressStruct: legData.DropoffAddressStruct
      ? String(legData.DropoffAddressStruct)
      : undefined,
    pickupPhone: String(legData.PickupPhone ?? ''),
    pickupPhoneExtension: String(legData.PickupPhoneExtension ?? ''),
    dropoffPhone: String(legData.DropoffPhone ?? ''),
    dropoffPhoneExtension: String(legData.DropoffPhoneExtension ?? ''),
    pickupNotes: String(
      legData.PickupNote ?? legData.pickUpNote ?? legData.pickUpNotes ?? '',
    ),
    dropoffNotes: String(
      legData.DropoffNote ?? legData.dropOffNote ?? legData.dropOffNotes ?? '',
    ),
    roundTripWillCall: Boolean(legData.RoundTripWillCall ?? true),
    roundTripReturnDate: roundTripReturnTime?.isValid
      ? (roundTripReturnTime.toISODate() ?? '')
      : '',
    roundTripReturnDateTime: roundTripReturnTime?.isValid
      ? roundTripReturnTime
      : null,
    passengerMobility: String(
      legData.passengerMobility ?? legData.AttributeLevel1 ?? '',
    ),
    passengerAssistance: String(legData.MobilityAttribute ?? ''),
    AttributeLevel1: String(legData.AttributeLevel1 ?? ''),
    AttributeLevel2: String(legData.AttributeLevel2 ?? ''),
    AttributeLevel3: String(legData.AttributeLevel3 ?? ''),
    AttributeLevel4: String(legData.AttributeLevel4 ?? ''),
    AttributeLevel5: String(legData.AttributeLevel5 ?? ''),
    AttributeLevel6: String(legData.AttributeLevel6 ?? ''),
    MobilityAttribute: String(legData.MobilityAttribute ?? ''),
    height: String(legData.height ?? ''),
    weight: String(legData.weight ?? ''),
    reservationReferenceID: String(legData.ReservationReferenceID ?? ''),
    tripLegReferenceID: String(legData.TripLegReferenceID ?? ''),
    roundTripTripLegReferenceID: String(
      legData.RoundTripTripLegReferenceID ?? '',
    ),
  };
}

function buildReservationFormValuesFromTripLeg(
  passenger: Passenger,
  reservationReferenceID: string,
  tripLeg: TripLeg,
  tripType: TripLegType,
  transportationAttributes?: TransportationAttribute[] | null,
): ReservationFormInput {
  const base = buildEmptyReservationFormValues(
    passenger,
    transportationAttributes,
  );
  const legDefaults = reservationUtils.getLegDefaults(
    passenger,
    reservationReferenceID,
    tripLeg,
    transportationAttributes ?? undefined,
  );
  const memberDefaults = reservationUtils.getMemberDefaults(
    tripLeg,
    transportationAttributes ?? undefined,
  );
  const override = tripLeg.overrides?.[0];
  const companionCount = memberDefaults.AdditionalPassengerCount;
  const hasRequestedProvider = Boolean(
    memberDefaults.RequestedTransportationProviderID,
  );

  return {
    ...base,
    ...memberDefaults,
    tripType,
    // Expansion toggles stay collapsed by default (nested values still hydrate).
    AdditionalAttributes: false,
    RequestedProvider: hasRequestedProvider,
    AdditionalPassengerCount:
      companionCount === '' || companionCount == null
        ? null
        : Number(companionCount),
    AllowOverride: Boolean(override),
    OverrideFirstName: override?.clientFirstName ?? '',
    OverrideLastName: override?.clientLastName ?? '',
    OverrideReason: override?.tripLegOverrideReasonBC ?? '',
    OverrideServicePlan: override?.servicePlanUUID ?? '',
    trips: [
      mapLegacyLegDataToTripSegment(
        legDefaults as unknown as Record<string, unknown>,
        tripType,
      ),
    ],
  };
}

/**
 * Create-form values copied from existing legs (Duplicate Trip), one trip
 * segment per leg. Member-step fields come from the first leg. Everything
 * carries over except the dates/times the user must pick again, the source
 * reference IDs (this is a new reservation) and the override request.
 */
function buildDuplicateReservationFormValues(
  passenger: Passenger,
  sourceTripLegs: TripLeg[],
  tripType: TripLegType,
  transportationAttributes?: TransportationAttribute[] | null,
): ReservationFormInput {
  const [firstLeg, ...otherLegs] = sourceTripLegs;
  const values = buildReservationFormValuesFromTripLeg(
    passenger,
    '',
    firstLeg,
    tripType,
    transportationAttributes,
  );
  const trips = [
    ...values.trips,
    ...otherLegs.map((leg) =>
      mapLegacyLegDataToTripSegment(
        reservationUtils.getLegDefaults(
          passenger,
          '',
          leg,
          transportationAttributes ?? undefined,
        ) as unknown as Record<string, unknown>,
        tripType,
      ),
    ),
  ];
  return {
    ...values,
    AllowOverride: false,
    OverrideFirstName: '',
    OverrideLastName: '',
    OverrideReason: '',
    OverrideServicePlan: '',
    trips: trips.map((trip) => ({
      ...trip,
      appointmentDate: '',
      appointmentDateTime: null,
      pickupDate: '',
      pickupDateTime: null,
      roundTripWillCall:
        tripType === TripLegType.RoundTrip ? trip.roundTripWillCall : true,
      roundTripReturnDate: '',
      roundTripReturnDateTime: null,
      reservationReferenceID: '',
      tripLegReferenceID: '',
      roundTripTripLegReferenceID: '',
    })),
  };
}

function buildEmptyReservationFormValues(
  passenger?: Passenger | null,
  transportationAttributes?: TransportationAttribute[] | null,
): ReservationFormInput {
  const { pickupPhone, pickupPhoneExtension } =
    getDefaultPickupPhoneAndExtensionFromPassenger(passenger);
  const primaryAttrs = reservationUtils.getPrimaryAttributeDefaults(
    undefined,
    transportationAttributes ?? undefined,
  );
  return {
    ...emptyReservationFormValues,
    MemberHomePhone:
      phoneUtils.getPassengerHomePhone(passenger?.passengerPhones ?? [])
        ?.phoneNumber ?? '',
    MemberMobilePhone:
      phoneUtils.getPassengerMobilePhone(passenger?.passengerPhones ?? [])
        ?.phoneNumber ?? '',
    AllowText:
      phoneUtils.getPassengerMobilePhone(passenger?.passengerPhones ?? [])
        ?.hasSMSConsent ?? false,
    trips: [
      {
        ...emptyTripSegment,
        pickupPhone,
        pickupPhoneExtension,
        ...primaryAttrs,
      },
    ],
  };
}

export {
  type CreatedReservationFormSchema,
  type ReservationFormInput,
  type ReservationFormValues,
  type TripSegmentValues,
  DEFAULT_TRIP_TYPE,
  MEMBER_FORM_PHONE_FIELDS,
  createReservationFormSchema,
  createTripLegReservationFormSchema,
  reservationFormSchema,
  tripLegReservationFormSchema,
  emptyReservationFormValues,
  buildEmptyReservationFormValues,
  buildReservationFormValuesFromTripLeg,
  buildDuplicateReservationFormValues,
  emptyTripSegment,
  tripSegmentSchema,
  isAppointmentDateRequired,
  isAppointmentDateTimeRequired,
  isPickupDateTimeRequired,
};
